import { Directive, inject, input, model, output } from '@angular/core';
import { FormFieldStateService } from '../services/form-field-state.service';

export interface FieldNameToIndex {
  [fieldName: string]: number;
}

@Directive({
  selector: '[sharedCoreNextInputOnKeydownDirective]',
  host: {
    '(window:keydown)': 'onKeyDown($event)',
  },
})
export class NextInputOnKeydownDirective {
  private readonly formFieldStateService = inject(FormFieldStateService);

  readonly listenerKeycode = input<string>('Enter');
  readonly currentIndex = model.required<number | undefined>();
  readonly orderedIdentifier = input.required<string[]>();
  readonly isActive = input.required();

  invalidKeydown = output<string>();
  reachedEndOfForm = output();

  onKeyDown(event: KeyboardEvent) {
    if (!this.isActive()) return;

    if (event.key !== this.listenerKeycode()) return;

    const currentIndex = this.currentIndex();
    if (currentIndex === undefined) return;
    if (!this.orderedIdentifier()) return;

    const currentFieldName = this.orderedIdentifier()![currentIndex];

    const fieldState = this.formFieldStateService.getFormFieldState(currentFieldName);

    if (fieldState?.invalid()) {
      this.invalidKeydown.emit(currentFieldName);
      fieldState?.touched.set(true);
      return;
    }

    if (currentIndex >= this.orderedIdentifier()!.length - 1) {
      this.currentIndex.set(undefined);
      this.reachedEndOfForm.emit();
      return;
    }

    const nextNotDisabledField = this.orderedIdentifier()!.findIndex((field, index) => {
      const fieldStateOfNextField = this.formFieldStateService.getFormFieldState(field);
      return index > currentIndex && !fieldStateOfNextField?.disabled();
    });

    if (nextNotDisabledField === -1) {
      this.currentIndex.set(undefined);
      this.reachedEndOfForm.emit();
      return;
    }

    this.currentIndex.set(nextNotDisabledField);
  }
}
